import { createSelector } from '@reduxjs/toolkit'
import { ServiceLimitType } from '@/models/service'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import {
  mapSelectedElementHasVisibleSeatsSelector,
  mapSelectedElementServices,
  mapSelectors,
} from '@/store/mapSlice'
import { mapVisibleTicketGroupsSelector } from '@/store/mapSlice/selectors/mapVisibleTicketGroupsSelector'
import { servicesSlice } from '@/store/servicesSlice'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import {
  getMapTicketGlobalQuantity,
  getSelectedElementTicketQuantity,
} from '@/utils/getMapTicketGlobalQuantity'
import { hasConfiguredMaximum } from '@/utils/getReservationConstraintMessage'
import { getServiceMinimumQuantity } from '@/utils/getServiceMinimumQuantity'
import { getServiceSingleLimit } from '@/utils/getServiceSingleLimit'
import { getVisibleServices } from '@/utils/getVisibleServices'
import { handleError } from '@/utils/handleError'
import { ReservationValidationError } from '@/utils/reservationValidationMessages'
import { sumServiceGroupSelectedQuantity } from '@/utils/sumServiceGroupSelectedQuantity'
import { getBoughtServicesTotal } from '@/utils/timeSchedules'
import { cartSlice } from '..'
import { activeElementQuotedSeatsAmountSelector } from './activeElementQuotedSeatsAmountSelector'

const SPOT_ERROR_TYPES = new Set([
  'minQty',
  'maxQty',
  'spotMinQty',
  'spotMaxQty',
  'globalSpotMinQty',
  'globalSpotMaxQty',
])

type MaxQtyErr = {
  type: 'maxQty'
  maxQty: number
}

type MinQtyErr = {
  type: 'minQty'
  minQty: number
}

type GroupMinQtyErr = {
  type: 'groupMinQty'
  minQty: number
  groupName: string
}

type GroupMaxQtyErr = {
  type: 'groupMaxQty'
  maxQty: number
  groupName: string
}

type GlobalMinQtyErr = {
  type: 'globalMinQty'
  minQty: number
}

type GlobalMaxQtyErr = {
  type: 'globalMaxQty'
  maxQty: number
}

type SpotMinQtyErr = {
  type: 'spotMinQty'
  minQty: number
  groupName: string
}

type SpotMaxQtyErr = {
  type: 'spotMaxQty'
  maxQty: number
  groupName: string
}

type GlobalSpotMinQtyErr = {
  type: 'globalSpotMinQty'
  minQty: number
}

type GlobalSpotMaxQtyErr = {
  type: 'globalSpotMaxQty'
  maxQty: number
}

type Errors =
  | MaxQtyErr
  | MinQtyErr
  | GroupMinQtyErr
  | GroupMaxQtyErr
  | GlobalMinQtyErr
  | GlobalMaxQtyErr
  | SpotMinQtyErr
  | SpotMaxQtyErr
  | GlobalSpotMinQtyErr
  | GlobalSpotMaxQtyErr

export type MapPageError = {
  groupId: number
  serviceId?: number
  serviceName?: string
} & Errors

export const mapPageErrorsSelector = createSelector(
  [
    cartSlice.selectors.selectedElementReservation,
    cartSlice.selectors.selectedElementQuotableProducts,
    cartSlice.selectors.cartReservations,
    mapSelectors.selectedElement,
    mapSelectedElementServices,
    mapSelectedElementHasVisibleSeatsSelector,
    mapVisibleTicketGroupsSelector,
    servicesSlice.selectors.data,
    dayDifferenceSelector,
    activeElementQuotedSeatsAmountSelector,
  ],
  (
    selectedElementReservation,
    selectedElementQuotableProducts,
    cartReservations,
    selectedElement,
    mapSelectedElementServices,
    selectedElementHasVisibleSeats,
    visibleTicketGroups,
    servicesData,
    dayDifference,
    selectedElementQuotedSeatsAmount
  ): MapPageError[] => {
    try {
      const errors: MapPageError[] = []
      if (!selectedElementReservation || !selectedElement) {
        return errors
      }

      const boughServicesMap = new Map<number, number>()

      Object.entries(selectedElementReservation.boughtServices).forEach(
        (entry) => {
          const [key, boughtServices] = entry
          const serviceId = Number(key)

          boughServicesMap.set(
            serviceId,
            getBoughtServicesTotal(boughtServices)
          )
        }
      )

      const visibleTicketServices = getVisibleServices({
        selectedElementHasVisibleSeats,
        services: mapSelectedElementServices.ticketServices,
      })

      visibleTicketServices.forEach((service) => {
        const boughtAmout = boughServicesMap.get(service.serviceId)

        const minimumQuantity = getServiceMinimumQuantity(
          service,
          selectedElementQuotedSeatsAmount,
          dayDifference
        )

        if (
          minimumQuantity > 0 &&
          (typeof boughtAmout === 'undefined' || boughtAmout < minimumQuantity)
        ) {
          errors.push({
            groupId: service.serviceGroupId,
            minQty: minimumQuantity,
            serviceId: service.serviceId,
            serviceName: service.name,
            type: 'minQty',
          })
        }

        if (service.maximumLimitType === ServiceLimitType.NUMBER_OF_PIECES) {
          const ticketLimit = getServiceSingleLimit({
            dayDifference,
            seatsAmount: selectedElementQuotedSeatsAmount,
            service,
            serviceInCartQuantity: boughtAmout,
          })

          if (typeof boughtAmout === 'number' && boughtAmout > ticketLimit) {
            errors.push({
              groupId: service.serviceGroupId,
              maxQty: ticketLimit,
              serviceId: service.serviceId,
              serviceName: service.name,
              type: 'maxQty',
            })
          }
        }
      })

      visibleTicketGroups.forEach((group) => {
        const groupTotal = sumServiceGroupSelectedQuantity({
          cartReservations,
          selectedElement,
          selectedElementQuotableProducts,
          service: group.services[0],
          ticketServices: visibleTicketServices,
        })

        if (
          group.minimumReservationQuantity > 0 &&
          groupTotal < group.minimumReservationQuantity
        ) {
          errors.push({
            groupId: group.serviceGroupId,
            groupName: group.name,
            minQty: group.minimumReservationQuantity,
            type: 'groupMinQty',
          })
        }

        if (
          hasConfiguredMaximum(group.maximumReservationQuantity) &&
          groupTotal > group.maximumReservationQuantity
        ) {
          errors.push({
            groupId: group.serviceGroupId,
            groupName: group.name,
            maxQty: group.maximumReservationQuantity,
            type: 'groupMaxQty',
          })
        }

        const groupSpotTotal = getGroupBoughtQuantity({
          groupId: group.serviceGroupId,
          selectedElementQuotableProducts,
          ticketServices: visibleTicketServices,
        })

        if (
          group.minimumSpotQuantity > 0 &&
          groupSpotTotal < group.minimumSpotQuantity
        ) {
          errors.push({
            groupId: group.serviceGroupId,
            groupName: group.name,
            minQty: group.minimumSpotQuantity,
            type: 'spotMinQty',
          })
        }

        if (
          hasConfiguredMaximum(group.maximumSpotQuantity) &&
          groupSpotTotal > group.maximumSpotQuantity
        ) {
          errors.push({
            groupId: group.serviceGroupId,
            groupName: group.name,
            maxQty: group.maximumSpotQuantity,
            type: 'spotMaxQty',
          })
        }
      })

      if (servicesData) {
        const globalTotal = getMapTicketGlobalQuantity({
          cartReservations,
          selectedElement,
          selectedElementQuotableProducts,
          ticketServices: visibleTicketServices,
        })

        const spotTotal = getSelectedElementTicketQuantity({
          selectedElementQuotableProducts,
          ticketServices: visibleTicketServices,
        })

        if (
          servicesData.reservationMinimumQuantity > 0 &&
          globalTotal < servicesData.reservationMinimumQuantity
        ) {
          errors.push({
            groupId: 0,
            minQty: servicesData.reservationMinimumQuantity,
            type: 'globalMinQty',
          })
        }

        if (
          hasConfiguredMaximum(servicesData.reservationLimitQuantity) &&
          globalTotal > servicesData.reservationLimitQuantity
        ) {
          errors.push({
            groupId: 0,
            maxQty: servicesData.reservationLimitQuantity,
            type: 'globalMaxQty',
          })
        }

        if (
          servicesData.spotMinimumQuantity > 0 &&
          spotTotal < servicesData.spotMinimumQuantity
        ) {
          errors.push({
            groupId: 0,
            minQty: servicesData.spotMinimumQuantity,
            type: 'globalSpotMinQty',
          })
        }

        if (
          servicesData.spotLimitQuantity > 0 &&
          spotTotal > servicesData.spotLimitQuantity
        ) {
          errors.push({
            groupId: 0,
            maxQty: servicesData.spotLimitQuantity,
            type: 'globalSpotMaxQty',
          })
        }
      }

      return errors
    } catch (error) {
      handleError(error, {
        tags: {
          selector: 'mapPageErrorsSelector',
        },
      })
    }

    return []
  }
)

export const mapPageSpotErrorsSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) => errors.filter((error) => SPOT_ERROR_TYPES.has(error.type))
)

export const mapPageReservationErrorsSelector = createSelector(
  [mapPageErrorsSelector],
  (errors): ReservationValidationError[] =>
    errors.filter(
      (error): error is MapPageError & ReservationValidationError =>
        error.type === 'groupMinQty' ||
        error.type === 'groupMaxQty' ||
        error.type === 'globalMinQty' ||
        error.type === 'globalMaxQty'
    )
)

export const mapPageGroupMinErrorsSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) => errors.filter((error) => error.type === 'groupMinQty')
)

export const mapPageGroupMaxErrorsSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) => errors.filter((error) => error.type === 'groupMaxQty')
)

export const mapPageGroupReservationErrorsSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) =>
    errors.filter(
      (error) => error.type === 'groupMinQty' || error.type === 'groupMaxQty'
    )
)

export const mapPageGroupSpotErrorsSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) =>
    errors.filter(
      (error) => error.type === 'spotMinQty' || error.type === 'spotMaxQty'
    )
)

export const mapPageGlobalMinErrorSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) => errors.find((error) => error.type === 'globalMinQty') ?? null
)

export const mapPageGlobalMaxErrorSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) =>
    errors.find(
      (error): error is GlobalMaxQtyErr & { groupId: number } =>
        error.type === 'globalMaxQty'
    ) ?? null
)

export const mapPageGlobalSpotMinErrorSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) => errors.find((error) => error.type === 'globalSpotMinQty') ?? null
)

export const mapPageGlobalSpotMaxErrorSelector = createSelector(
  [mapPageErrorsSelector],
  (errors) => errors.find((error) => error.type === 'globalSpotMaxQty') ?? null
)
